import api, { isSuccessResponse } from "lib/api";
import { API_ENDPOINTS } from "lib/apiUrl";
import {
  getInvestmentPaymentStatus,
  getInvestmentRecordId,
} from "lib/investmentListHelpers";

export const CANCELLATION_WORKING_DAYS = 2;

const CANCELLABLE_STATUSES = new Set(["pending", "paid"]);

export function getInvestmentCreatedAt(investment) {
  return investment?.createdAt ?? investment?.dateInvested ?? null;
}

function isWorkingDay(date) {
  const day = date.getDay();
  return day >= 1 && day <= 5;
}

/**
 * First day included in the cancellation window: creation day if Mon–Fri,
 * otherwise the next Monday (investments created on weekends start counting then).
 */
function getCancellationCountStart(createdAt) {
  const start = new Date(createdAt);
  start.setHours(0, 0, 0, 0);
  while (!isWorkingDay(start)) {
    start.setDate(start.getDate() + 1);
  }
  return start;
}

/**
 * Working days elapsed since investment creation (inclusive of the count start day).
 * @param {string|Date} createdAt - investment `createdAt` / `dateInvested`
 * @param {Date} [asOf=new Date()] - usually "now"
 */
export function countWorkingDaysSinceCreation(createdAt, asOf = new Date()) {
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return Number.POSITIVE_INFINITY;

  const start = getCancellationCountStart(created);
  const end = new Date(asOf);
  end.setHours(23, 59, 59, 999);

  if (start > end) return 0;

  let count = 0;
  let dayMs = start.getTime();
  const endMs = end.getTime();
  const oneDayMs = 24 * 60 * 60 * 1000;

  while (dayMs <= endMs) {
    if (isWorkingDay(new Date(dayMs))) count += 1;
    dayMs += oneDayMs;
  }

  return count;
}

export function getCancellationRequestStatus(investment) {
  return String(
    investment?.cancellationRequestStatus ?? investment?.cancellationRequest?.status ?? ""
  )
    .trim()
    .toLowerCase();
}

export function isCancellationRequestRequested(investment) {
  return getCancellationRequestStatus(investment) === "requested";
}

export const CANCELLATION_REQUESTED_HINT = "Cancellation requested";

export function hasPendingCancellationRequest(investment) {
  if (isCancellationRequestRequested(investment)) return true;

  return Boolean(
    investment?.cancellationRequestedAt ||
      investment?.cancellationRequestId ||
      investment?.cancellationNotes
  );
}

/** Show cancel when within the NI 45-110 withdrawal window (second business day; approx. 2 working days). */
export function canRequestInvestmentCancellation(investment) {
  if (!investment || hasPendingCancellationRequest(investment)) return false;

  const status = getInvestmentPaymentStatus(investment);
  if (!CANCELLABLE_STATUSES.has(status)) return false;

  const createdRaw = getInvestmentCreatedAt(investment);
  if (!createdRaw) return false;

  const created = new Date(createdRaw);
  if (Number.isNaN(created.getTime())) return false;

  const workingDaysSinceCreation = countWorkingDaysSinceCreation(created);
  return workingDaysSinceCreation <= CANCELLATION_WORKING_DAYS;
}

export function getAuthUserEmail(user) {
  return String(user?.email ?? "").trim();
}

/**
 * POST cancellation request for an investment.
 * @returns {{ ok: true, message: string } | { ok: false, message: string }}
 */
export async function submitInvestmentCancellationRequest(investment, { notes }) {
  const investmentId = getInvestmentRecordId(investment);
  if (!investmentId) {
    return { ok: false, message: "Investment id is missing." };
  }

  const trimmedNotes = String(notes ?? "").trim();
  if (!trimmedNotes) {
    return { ok: false, message: "Cancellation reason is required." };
  }

  const resp = await api.post(API_ENDPOINTS.investmentCancellationRequest(investmentId), {
    notes: trimmedNotes,
  });

  if (!isSuccessResponse(resp)) {
    const message =
      (resp && typeof resp === "object" && (resp.message || resp.error)) ||
      "Could not submit cancellation request.";
    return { ok: false, message };
  }

  const message =
    (resp && typeof resp === "object" && resp.message) ||
    "Your cancellation request was submitted successfully.";

  return { ok: true, message };
}
